import { useTranslation } from '@multaqa/i18n'
import { Input } from '@multaqa/ui/components/ui/input'
import { Label } from '@multaqa/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { Switch } from '@multaqa/ui/components/ui/switch'
import type { GroupDefaults as GroupDefaultsData, GroupDefaultsUpdateInput } from './api'

export type SettingsFormValues = GroupDefaultsUpdateInput
type SystemDefaults = GroupDefaultsData['system_defaults']

export function SettingsFields({
  values,
  system,
  disabled,
  onChange,
}: {
  values: SettingsFormValues
  system: SystemDefaults
  disabled: boolean
  onChange: <K extends keyof SettingsFormValues>(key: K, value: SettingsFormValues[K]) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="group-defaults-settings-form">
      <NumberField
        id="group-defaults-payment-terms"
        label={t('groups.defaults.settings.fields.paymentTerms')}
        value={values.default_payment_terms_days}
        system={system.default_payment_terms_days}
        min="0"
        disabled={disabled}
        onChange={(value) => onChange('default_payment_terms_days', value)}
      />
      <NumberField
        id="group-defaults-expiry-warning"
        label={t('groups.defaults.settings.fields.expiryWarningDays')}
        value={values.expiry_warning_days}
        system={system.expiry_warning_days}
        min="0"
        disabled={disabled}
        onChange={(value) => onChange('expiry_warning_days', value)}
      />
      <SelectField
        id="group-defaults-expiry-convention"
        label={t('groups.defaults.settings.fields.expiryConvention')}
        value={values.default_expiry_convention}
        system={system.default_expiry_convention}
        options={[
          { value: 'on', label: t('groups.defaults.settings.values.on') },
          { value: 'after', label: t('groups.defaults.settings.values.after') },
        ]}
        disabled={disabled}
        onChange={(value) => onChange('default_expiry_convention', value as 'on' | 'after' | null)}
      />
      <SelectField
        id="group-defaults-allocation"
        label={t('groups.defaults.settings.fields.allocationStrategy')}
        value={values.default_allocation_strategy}
        system={system.default_allocation_strategy}
        options={[
          { value: 'fefo', label: t('groups.defaults.settings.values.fefo') },
          { value: 'fifo', label: t('groups.defaults.settings.values.fifo') },
        ]}
        disabled={disabled}
        onChange={(value) =>
          onChange('default_allocation_strategy', value as 'fefo' | 'fifo' | null)
        }
      />
      <SelectField
        id="group-defaults-over-receipt"
        label={t('groups.defaults.settings.fields.overReceiptPolicy')}
        value={values.over_receipt_policy}
        system={system.over_receipt_policy}
        options={[
          { value: 'block', label: t('groups.defaults.settings.values.block') },
          { value: 'allow', label: t('groups.defaults.settings.values.allow') },
        ]}
        disabled={disabled}
        onChange={(value) => onChange('over_receipt_policy', value as 'block' | 'allow' | null)}
      />
      <NumberField
        id="group-defaults-permission-minutes"
        label={t('groups.defaults.settings.fields.permissionMonthlyMinutes')}
        value={values.permission_monthly_minutes}
        system={system.permission_monthly_minutes}
        min="1"
        disabled={disabled}
        onChange={(value) => onChange('permission_monthly_minutes', value)}
      />
      <TextField
        id="group-defaults-accent-color"
        label={t('groups.defaults.settings.fields.brandingAccentColor')}
        value={values.branding_accent_color}
        system={system.branding_accent_color}
        disabled={disabled}
        onChange={(value) => onChange('branding_accent_color', value)}
      />
      <div className="flex min-h-8 items-center justify-between gap-3 border-b border-border pb-2">
        <div>
          <Label htmlFor="group-defaults-negative-stock">
            {t('groups.defaults.settings.fields.allowNegativeStock')}
          </Label>
          {values.allow_negative_stock == null ? (
            <SystemHint value={system.allow_negative_stock} />
          ) : null}
        </div>
        <Switch
          id="group-defaults-negative-stock"
          checked={values.allow_negative_stock ?? system.allow_negative_stock}
          disabled={disabled}
          onCheckedChange={(value) => onChange('allow_negative_stock', value)}
        />
      </div>
    </div>
  )
}

function NumberField({
  id,
  label,
  value,
  system,
  min,
  disabled,
  onChange,
}: {
  id: string
  label: string
  value: number | null | undefined
  system: unknown
  min: string
  disabled: boolean
  onChange: (value: number | null) => void
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {value == null ? <SystemHint value={system} /> : null}
      <Input
        id={id}
        data-testid={id}
        type="number"
        min={min}
        step="1"
        numeric
        value={value ?? ''}
        disabled={disabled}
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
      />
    </div>
  )
}
function SelectField({
  id,
  label,
  value,
  system,
  options,
  disabled,
  onChange,
}: {
  id: string
  label: string
  value: string | null | undefined
  system: unknown
  options: { value: string; label: string }[]
  disabled: boolean
  onChange: (value: string | null) => void
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {value == null ? <SystemHint value={system} /> : null}
      <Select value={value ?? ''} onValueChange={(next) => onChange(next || null)}>
        <SelectTrigger id={id} data-testid={id} disabled={disabled}>
          <SelectValue placeholder="" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
function TextField({
  id,
  label,
  value,
  system,
  disabled,
  onChange,
}: {
  id: string
  label: string
  value: string | null | undefined
  system: unknown
  disabled: boolean
  onChange: (value: string | null) => void
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {value == null ? <SystemHint value={system} /> : null}
      <Input
        id={id}
        data-testid={id}
        value={value ?? ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value || null)}
      />
    </div>
  )
}
export function SystemHint({ value }: { value: unknown }) {
  const { t } = useTranslation()
  const text =
    typeof value === 'boolean'
      ? t(`groups.defaults.settings.values.${value ? 'enabled' : 'disabled'}`)
      : value == null
        ? t('groups.defaults.settings.values.none')
        : ['on', 'after', 'fefo', 'fifo', 'block', 'allow'].includes(String(value))
          ? t(`groups.defaults.settings.values.${String(value)}`)
          : String(value)
  return (
    <span className="text-small text-muted-foreground">
      {t('groups.defaults.settings.systemDefault', { value: text })}
    </span>
  )
}
